import { supabase } from '../lib/supabase';
import { Formatters } from '../shared/lib/formatters';
import { CONFIRMED_STATUSES, BROWSABLE_STATUSES } from '../shared/lib/reservationStatus';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';

export class ReservationsPage {
  private container: HTMLElement;
  private reservations: any[]  = [];
  private chartData: any[]     = [];
  private market: { adr: number | null; occupancy: number | null; sample: number; year: number } | null = null;
  private listings: { id: string; name: string }[] = [];
  private selectedListingId: string = '';
  private currentPage: number = 1;
  private readonly itemsPerPage: number = 25;
  // Client-side table filters (applied over already-loaded `this.reservations`).
  private tableSearch: string = '';
  private tableChannel: string = 'all';
  private chartFromMonth: string;
  private chartToMonth: string;

  constructor(container: HTMLElement) {
    this.container = container;
    const now = new Date();
    const f = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const t = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    this.chartFromMonth = fmt(f);
    this.chartToMonth   = fmt(t);
  }

  private defaultDates(): { start: string; end: string } {
    const gdr = (window as any).globalDateRange;
    if (gdr?.from && gdr?.to) return { start: gdr.from, end: gdr.to };
    const localDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const threeMonths = new Date(today);
    threeMonths.setMonth(threeMonths.getMonth() + 3);
    return { start: localDate(today), end: localDate(threeMonths) };
  }

  async render(): Promise<void> {
    const { start, end } = this.defaultDates();
    const _now = new Date();
    const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;

    this.container.innerHTML = `
      <div class="page-content">

        <style>
          /* ── Light-theme the chart card to match the rest of the app
             (was a dark #0f172a surface that broke the visual system). ── */
          .res-chart-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:18px; }
          .res-chart-header { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
          .res-chart-title { color:var(--text); font-weight:600; font-size:15px; letter-spacing:-0.01em; }
          .res-chart-controls { display:flex; align-items:center; gap:18px; flex-wrap:wrap; }
          .res-metric-group { display:flex; align-items:center; gap:8px; }
          .res-metric-label { color:var(--text-muted); text-transform:uppercase; font-size:10px; font-weight:600; letter-spacing:.05em; }
          .res-metric-select { border:1px solid var(--border); background:var(--card); color:var(--text); border-radius:8px; padding:5px 8px; font-size:12px; transition:border-color .12s ease, box-shadow .12s ease; }
          .res-metric-select:hover { border-color:var(--text-faint); }
          .res-metric-select:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
          .res-kpi-strip { border-top:1px solid var(--border); border-bottom:1px solid var(--border); grid-template-columns:repeat(auto-fit, minmax(140px, 1fr)) !important; }
          .res-kpi-item { border-right:1px solid var(--border-soft); padding:14px 16px; }
          .res-kpi-label { color:var(--text-muted); text-transform:uppercase; font-size:10.5px; font-weight:600; letter-spacing:.05em; }
          .res-kpi-value { color:var(--text); min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-weight:600; font-variant-numeric:tabular-nums; letter-spacing:-0.01em; margin-top:4px; }
          /* Market-context KPIs keep the indigo accent for emphasis. */
          #kpi-mkt-adr-item { border-left:2px solid var(--accent-border) !important; }
          /* market context reads as ink like everything else — the label says what it is */
          #kpi-mkt-adr-item .res-kpi-label, #kpi-mkt-occ-item .res-kpi-label { color:var(--text-muted) !important; }

          /* ── Today banner cards — soft white panels with gentle elevation ── */
          .today-banner { grid-template-columns:repeat(2, minmax(0, 1fr)); gap:14px; }
          .today-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:16px 18px; }
          .today-card-title { color:var(--text); font-weight:600; font-size:13px; display:flex; align-items:center; gap:8px; margin-bottom:10px; }
          .today-guest-row { display:flex; align-items:center; justify-content:space-between; padding:8px 0; border-bottom:1px solid var(--border-soft); }
          .today-guest-row:last-child { border-bottom:none; }
          .today-guest-name { color:var(--text); font-weight:500; font-size:13px; }
          .today-guest-prop { color:var(--text-muted); font-size:12px; margin-top:2px; }
          .today-empty { color:var(--text-faint); font-size:13px; padding:6px 0; }

          /* ── Table container — clean card with crisp header rule ── */
          .table-container { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); overflow:hidden; }
          .table-header { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; padding:16px 18px; border-bottom:1px solid var(--border); }
          .table-header h3 { color:var(--text); font-weight:600; font-size:15px; letter-spacing:-0.01em; margin:0; }
          .table-container thead th { text-transform:uppercase; font-size:10.5px; font-weight:600; letter-spacing:.05em; color:var(--text-muted); padding:10px 14px; border-bottom:1px solid var(--border); background:var(--bg-subtle); white-space:nowrap; }
          .table-container tbody td { font-size:12.5px; color:var(--text); padding:11px 14px; border-bottom:1px solid var(--border-soft); }
          .table-container tbody tr:last-child td { border-bottom:none; }
          .table-container tbody tr.res-row:hover { background:var(--bg-subtle); }

          /* Overflow-safe override for the legacy bare-1fr today banner. */
          @media (max-width:640px) {
            .today-banner { grid-template-columns:1fr; }
            .res-chart-controls { gap:10px; }
            .res-metric-group { flex-wrap:wrap; }
            .res-chart-body { height:300px !important; }
          }
        </style>

        <div class="dash-head">
          <h1>Reservations</h1>
          <p>Every booking across the portfolio, with a trend view of revenue, nights, and rate — see what is on the books and how it is pacing.</p>
        </div>

        <!-- ── Chart card ──────────────────────────────── -->
        <div class="res-chart-card">
          <div class="res-chart-header">
            <div class="res-chart-title">Reservation KPIs <span id="res-chart-range" style="color:var(--text-muted); font-weight:400; font-size:12px;"></span></div>
            <div class="res-chart-controls">
              <div class="res-metric-group">
                <span class="res-metric-label">Period</span>
                <div style="display:flex; align-items:center; gap:4px;">
                  <input type="month" id="res-chart-from" class="res-metric-select" value="${this.chartFromMonth}" style="font-size:12px; padding:4px 6px;">
                  <span style="color:var(--text-muted); font-size:12px;">→</span>
                  <input type="month" id="res-chart-to" class="res-metric-select" value="${this.chartToMonth}" style="font-size:12px; padding:4px 6px;">
                </div>
              </div>
              <div class="res-metric-group">
                <span class="res-metric-label">Property</span>
                <select id="res-property-filter" class="res-metric-select" style="min-width:160px;">
                  <option value="">All Properties</option>
                </select>
              </div>
            </div>
          </div>

          <!-- KPI band -->
          <div class="kpi-grid" id="res-kpi-strip">
            <div class="kpi-card">
              <div class="kpi-card-top"><span class="kpi-card-label">Total Revenue ${infoIcon('gross_revenue')}</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-revenue">—</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-card-top"><span class="kpi-card-label">Total Nights ${infoIcon('booked_nights')}</span><span class="kpi-card-ic">${icon('calendar', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-nights">—</div>
            </div>
            <div class="kpi-card">
              <div class="kpi-card-top"><span class="kpi-card-label">Reservations ${infoIcon('reservation_count')}</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-count">—</div>
            </div>
            <div class="kpi-card" id="kpi-adr-item">
              <div class="kpi-card-top"><span class="kpi-card-label">Avg Nightly Rate ${infoIcon('adr')}</span><span class="kpi-card-ic">${icon('trending', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-adr">—</div>
            </div>
            <div class="kpi-card" id="kpi-mkt-adr-item" style="display:none;">
              <div class="kpi-card-top"><span class="kpi-card-label">Market ADR ${infoIcon('market_adr')}</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-mkt-adr">—</div>
            </div>
            <div class="kpi-card" id="kpi-mkt-occ-item" style="display:none;">
              <div class="kpi-card-top"><span class="kpi-card-label">Market Occupancy ${infoIcon('occupancy')}</span><span class="kpi-card-ic">${icon('home', 17)}</span></div>
              <div class="kpi-card-value" id="kpi-mkt-occ">—</div>
            </div>
          </div>

          <!-- The Portfolio Metrics combo chart moved to Money → Performance
               (shared/components/PortfolioMetricsChart) — it's a portfolio
               financial view, not a reservations utility. The KPI strip above
               stays: it summarizes the window/property selected here. -->
        </div>

        <!-- ── Today banner ────────────────────────────── -->
        <div class="today-banner">
          <div class="today-card">
            <div class="today-card-title"><span>🛬</span> Today's Arrivals</div>
            <div id="today-arrivals-list">${LoadingSpinner.render()}</div>
          </div>
          <div class="today-card">
            <div class="today-card-title"><span>🛫</span> Today's Departures</div>
            <div id="today-departures-list">${LoadingSpinner.render()}</div>
          </div>
        </div>

        <!-- ── Table ───────────────────────────────────── -->
        <div class="table-container">
          <div class="table-header">
            <h3>Reservations <span style="color:var(--text-muted); font-weight:400;">(<span id="reservations-count">0</span>)</span></h3>
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
              <div style="position:relative;">
                <span style="position:absolute; left:9px; top:50%; transform:translateY(-50%); font-size:12px; color:var(--text-muted); pointer-events:none; line-height:1;">🔍</span>
                <input type="search" id="res-table-search" placeholder="Search guest or property…" value="${Formatters.escapeHtml(this.tableSearch)}"
                  style="padding:5px 8px 5px 28px; border:1px solid var(--border); border-radius:6px; font-size:12px; min-width:180px; color:var(--text);">
              </div>
              <div style="display:flex; align-items:center; gap:6px;">
                <input type="date" id="res-start-date" value="${start}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
                <span style="color:var(--text-muted); font-size:12px;">→</span>
                <input type="date" id="res-end-date" value="${end}" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px;">
              </div>
              <select id="res-status-filter" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; color:var(--text);">
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="modified">Modified</option>
                <option value="confirmed">Confirmed</option>
                <option value="reserved">Reserved</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select id="res-channel-filter" style="padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; color:var(--text);">
                <option value="all">All Channels</option>
              </select>
            </div>
          </div>
          <div class="table-scroll">
            <table style="min-width:700px;">
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Property</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th style="text-align:center;">Nights</th>
                  <th style="text-align:right;">Total</th>
                  <th>Channel</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody id="reservations-table-body">
                <tr><td colspan="8" style="padding:32px;">${LoadingSpinner.render()}</td></tr>
              </tbody>
            </table>
          </div>
          <div style="display:flex; justify-content:space-between; align-items:center; padding:14px 16px; border-top:1px solid var(--border);">
            <div style="font-size:13px; color:var(--text-muted);">
              Showing <span id="res-start">0</span>–<span id="res-end">0</span> of <span id="res-total">0</span>
            </div>
            <div style="display:flex; gap:8px; align-items:center;">
              <button class="btn btn-sm btn-secondary" id="res-prev-btn" disabled>← Prev</button>
              <span id="res-page-label" style="font-size:13px; color:var(--text-muted);"></span>
              <button class="btn btn-sm btn-secondary" id="res-next-btn">Next →</button>
            </div>
          </div>
        </div>

        ${PageFooter.render([
          { abbr: 'Check In',  full: 'Check-In Date',      note: 'guest arrival date' },
          { abbr: 'Check Out', full: 'Check-Out Date',     note: 'guest departure date' },
          { abbr: 'Nights',    full: 'Length of Stay',     note: 'nights between check-in and check-out' },
          { abbr: 'Total',     full: 'Total Revenue',      note: 'gross revenue including all fees' },
          { abbr: 'Channel',   full: 'Booking Channel',    note: 'platform through which the reservation was made' },
          { abbr: 'Status',    full: 'Reservation Status', note: 'confirmed = active; cancelled/declined = excluded from revenue' },
        ])}
      </div>

      <!-- Reservation detail drawer -->
      <div id="res-drawer-overlay" class="pf-overlay"></div>
      <aside id="res-drawer" class="pf-drawer">
        <div id="res-drawer-content"></div>
      </aside>
    `;

    await Promise.all([
      this.loadChartData(),
      this.loadTodayData(today),
      this.loadData(),
    ]);
    this.bindEvents();

    document.addEventListener('globalDateChange', () => {
      const gdr = (window as any).globalDateRange;
      if (!gdr) return;
      const startEl = document.getElementById('res-start-date') as HTMLInputElement;
      const endEl   = document.getElementById('res-end-date')   as HTMLInputElement;
      if (startEl) startEl.value = gdr.from;
      if (endEl)   endEl.value   = gdr.to;
      this.loadData();
    }, { once: true });
  }

  // ── Chart data: fetched & aggregated server-side (no row limit) ──────────

  private async loadChartData(listingId?: string): Promise<void> {
    try {
      const from = this.chartFromMonth + '-01';
      const [toY, toM] = this.chartToMonth.split('-').map(Number);
      const to   = new Date(toY, toM, 0).toISOString().split('T')[0]; // last day of toMonth
      const url   = `/api/reservations/chart-data?from=${from}&to=${to}${listingId ? `&listing_id=${listingId}` : ''}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      this.chartData = json.monthly || [];
      // AirROI market context (absent on older responses → degrade gracefully)
      this.market = (json.market && (json.market.adr != null || json.market.occupancy != null)) ? json.market : null;

      // Populate property dropdown only on initial (all-properties) load
      if (!listingId && json.listings?.length) {
        this.listings = json.listings;
        const select = document.getElementById('res-property-filter') as HTMLSelectElement | null;
        if (select) {
          select.innerHTML = `<option value="">All Properties</option>` +
            this.listings.map(l => `<option value="${l.id}">${Formatters.escapeHtml(l.name)}</option>`).join('');
        }
      }

      this.renderKPIs();
    } catch (err) {
      console.error('Chart data error:', err);
    }
  }

  private renderKPIs(): void {
    let revenue = 0, nights = 0, count = 0;
    for (const d of this.chartData as any[]) {
      revenue += d.revenue || 0;
      nights  += d.nights  || 0;
      count   += d.count   || 0;
    }
    const adr = nights > 0 ? revenue / nights : 0;
    const setText = (id: string, v: string) => { const e = document.getElementById(id); if (e) e.textContent = v; };
    setText('kpi-revenue', Formatters.formatCurrency(revenue));
    setText('kpi-nights',  nights.toLocaleString());
    setText('kpi-count',   count.toLocaleString());
    setText('kpi-adr',     Formatters.formatCurrency(adr));

    // Blended ADR is the number the how-to tells you to compare to Market ADR
    // — show it on the portfolio view too (it was hidden there, which left
    // Market ADR with nothing to compare against).
    const adrItem = document.getElementById('kpi-adr-item');
    if (adrItem) adrItem.style.display = '';

    // ── Market context KPIs (AirROI) — shown only when present ──
    const mktAdrItem = document.getElementById('kpi-mkt-adr-item');
    const mktOccItem = document.getElementById('kpi-mkt-occ-item');
    if (mktAdrItem) {
      if (this.market?.adr != null) {
        setText('kpi-mkt-adr', Formatters.formatCurrency(this.market.adr));
        mktAdrItem.style.display = '';
      } else {
        mktAdrItem.style.display = 'none';
      }
    }
    if (mktOccItem) {
      if (this.market?.occupancy != null && Number.isFinite(Number(this.market.occupancy))) {
        setText('kpi-mkt-occ', `${this.market.occupancy}%`);
        mktOccItem.style.display = '';
      } else {
        mktOccItem.style.display = 'none';
      }
    }
  }

  // ── Today ────────────────────────────────────────────────────────────────

  private async loadTodayData(today: string): Promise<void> {
    try {
      const [arrRes, depRes] = await Promise.all([
        supabase.from('reservations').select('guest_name, listings(name)').eq('check_in', today).in('status', [...CONFIRMED_STATUSES]).order('guest_name').limit(200),
        supabase.from('reservations').select('guest_name, listings(name)').eq('check_out', today).in('status', [...CONFIRMED_STATUSES]).order('guest_name').limit(200),
      ]);
      this.renderTodayList('today-arrivals-list',   arrRes.data || []);
      this.renderTodayList('today-departures-list', depRes.data || []);
    } catch {
      document.getElementById('today-arrivals-list')!.innerHTML   = '<p class="today-empty">—</p>';
      document.getElementById('today-departures-list')!.innerHTML = '<p class="today-empty">—</p>';
    }
  }

  private renderTodayList(elId: string, rows: any[]): void {
    const el = document.getElementById(elId);
    if (!el) return;
    if (!rows.length) { el.innerHTML = '<p class="today-empty">None today</p>'; return; }
    el.innerHTML = rows.map(r => `
      <div class="today-guest-row">
        <div>
          <div class="today-guest-name">${Formatters.escapeHtml(r.guest_name || 'Guest')}</div>
          <div class="today-guest-prop">${Formatters.escapeHtml(r.listings?.name || '—')}</div>
        </div>
      </div>
    `).join('');
  }

  // ── Table ────────────────────────────────────────────────────────────────

  private async loadData(): Promise<void> {
    const tbody = document.getElementById('reservations-table-body');
    if (tbody) tbody.innerHTML = `<tr><td colspan="8" style="padding:32px;">${LoadingSpinner.render()}</td></tr>`;
    try {
      const startDate    = (document.getElementById('res-start-date')    as HTMLInputElement)?.value;
      const endDate      = (document.getElementById('res-end-date')      as HTMLInputElement)?.value;
      const statusFilter = (document.getElementById('res-status-filter') as HTMLSelectElement)?.value;

      const PAGE = 1000;
      const all: any[] = [];
      let offset = 0;
      while (true) {
        let q = supabase.from('reservations').select('*, listings(name)');
        if (startDate)    q = q.gte('check_in', startDate);
        if (endDate)      q = q.lte('check_in', endDate);
        // "All Statuses" means bookings + cancellations — never the ~1k Hostaway
        // inquiries/pending rows, which would double the table and its totals.
        if (statusFilter) q = q.eq('status', statusFilter);
        else              q = q.in('status', [...BROWSABLE_STATUSES]);
        const { data, error } = await q.order('check_in', { ascending: true }).range(offset, offset + PAGE - 1);
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < PAGE) break;
        offset += PAGE;
      }
      this.reservations = all;
      this.currentPage  = 1;
      this.populateChannelFilter();
      this.renderTable();
    } catch (err) {
      console.error('Failed to load reservations:', err);
      const tb = document.getElementById('reservations-table-body');
      if (tb) tb.innerHTML = `<tr><td colspan="8" style="padding:32px; text-align:center; color:var(--text-muted);">
        Failed to load. <button class="btn btn-sm btn-secondary" onclick="location.reload()">Retry</button>
      </td></tr>`;
    }
  }

  // Map a reservation status to a shared .pill tone class.
  private statusPillClass(status: string): string {
    const s = (status || 'confirmed').toLowerCase();
    if (s === 'confirmed' || s === 'reserved') return 'pill-green';
    if (s === 'cancelled' || s === 'declined') return 'pill-red';
    if (s === 'modified')                       return 'pill-amber';
    if (s === 'new')                            return 'pill-blue';
    return 'pill-gray';
  }

  private channelColor(channel: string): string {
    const c = channel.toLowerCase();
    if (c.includes('airbnb'))                        return '#FF5A5F';
    if (c.includes('vrbo') || c.includes('homeaway')) return '#3D5AFE';
    if (c.includes('booking'))                        return '#003580';
    if (c.includes('direct'))                         return '#16a34a';
    return '#64748b';
  }

  // Channel label for a reservation (mirrors the table cell logic).
  private channelOf(res: any): string {
    return res.channel_name || res.source || '—';
  }

  // Rebuild the channel dropdown from the channels present in the loaded rows,
  // preserving the current selection when still valid.
  private populateChannelFilter(): void {
    const select = document.getElementById('res-channel-filter') as HTMLSelectElement | null;
    if (!select) return;
    const channels = Array.from(new Set(
      this.reservations.map(r => this.channelOf(r)).filter(c => c && c !== '—'),
    )).sort();
    if (!channels.includes(this.tableChannel)) this.tableChannel = 'all';
    select.innerHTML = `<option value="all">All Channels</option>` +
      channels.map(c => `<option value="${Formatters.escapeHtml(c)}"${c === this.tableChannel ? ' selected' : ''}>${Formatters.escapeHtml(c)}</option>`).join('');
  }

  // Apply the client-side search + channel filters over the loaded rows.
  private filteredReservations(): any[] {
    const q = this.tableSearch.trim().toLowerCase();
    return this.reservations.filter(res => {
      if (this.tableChannel !== 'all' && this.channelOf(res) !== this.tableChannel) return false;
      if (q) {
        const guest    = String(res.guest_name || '').toLowerCase();
        const property = String(res.listings?.name || res.listing_name || '').toLowerCase();
        if (!guest.includes(q) && !property.includes(q)) return false;
      }
      return true;
    });
  }

  private renderTable(): void {
    const tbody = document.getElementById('reservations-table-body');
    if (!tbody) return;
    const rows    = this.filteredReservations();
    const total   = rows.length;
    const countEl = document.getElementById('reservations-count');
    if (countEl) countEl.textContent = String(total);

    if (!total) {
      tbody.innerHTML = `<tr><td colspan="8" style="padding:0;">${emptyState(
        'No reservations found',
        'No reservations match the current search, dates, status, or channel filters. Try clearing the search or widening the date range.',
        '📅',
      )}</td></tr>`;
      this.updatePagination(0);
      return;
    }

    // Clamp the current page after filtering may have shrunk the list.
    const pages = Math.max(1, Math.ceil(total / this.itemsPerPage));
    if (this.currentPage > pages) this.currentPage = pages;

    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end   = Math.min(start + this.itemsPerPage, total);
    const page  = rows.slice(start, end);
    const _t = new Date();
    const today = `${_t.getFullYear()}-${String(_t.getMonth() + 1).padStart(2, '0')}-${String(_t.getDate()).padStart(2, '0')}`;

    tbody.innerHTML = page.map((res: any) => {
      const nights = res.check_in && res.check_out
        ? Math.round((new Date(res.check_out).getTime() - new Date(res.check_in).getTime()) / 86400000)
        : '—';
      const property    = res.listings?.name || res.listing_name || '—';
      const channel     = res.channel_name || res.source || '—';
      const isArriving  = res.check_in  === today;
      const isDeparting = res.check_out === today;
      const rowBg       = isArriving ? 'background:var(--success-soft);' : isDeparting ? 'background:var(--warning-soft);' : '';
      const totalRaw    = res.total_price ?? res.total_amount ?? res.total;
      const totalCell   = (totalRaw != null && Number.isFinite(Number(totalRaw))) ? Formatters.formatCurrency(Number(totalRaw)) : '—';

      return `
        <tr class="res-row" data-id="${res.id}" style="${rowBg} cursor:pointer; transition:background 0.1s;">
          <td style="font-weight:500;">
            ${Formatters.escapeHtml(res.guest_name || 'Guest')}
            ${isArriving  ? '<span style="font-size:10px;background:var(--success-soft);color:var(--success-text);padding:1px 5px;border-radius:8px;margin-left:4px;">arriving</span>' : ''}
            ${isDeparting ? '<span style="font-size:10px;background:var(--warning-soft);color:var(--warning-text);padding:1px 5px;border-radius:8px;margin-left:4px;">departing</span>' : ''}
          </td>
          <td style="max-width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;color:var(--text-muted);" title="${Formatters.escapeHtml(property)}">${Formatters.escapeHtml(property)}</td>
          <td style="font-size:13px;">${Formatters.formatDate(res.check_in)}</td>
          <td style="font-size:13px;">${Formatters.formatDate(res.check_out)}</td>
          <td style="text-align:center;font-size:13px;">${nights}</td>
          <td style="text-align:right;font-weight:600;font-variant-numeric:tabular-nums;">${totalCell}</td>
          <td><span style="font-size:11px;font-weight:600;color:${this.channelColor(channel)};">${Formatters.escapeHtml(channel)}</span></td>
          <td><span class="pill ${this.statusPillClass(res.status)}">${res.status || 'confirmed'}</span></td>
        </tr>
      `;
    }).join('');

    tbody.querySelectorAll<HTMLTableRowElement>('.res-row').forEach(row => {
      row.addEventListener('mouseenter', () => { if (!row.style.background) row.style.background = '#f8f7ff'; });
      row.addEventListener('mouseleave', () => { if (row.style.background === 'rgb(248, 247, 255)') row.style.background = ''; });
      row.addEventListener('click', () => {
        const id = row.dataset.id;
        const res = this.reservations.find(r => r.id === id);
        if (res) this.openDrawer(res);
      });
    });

    this.updatePagination(total);
  }

  private updatePagination(total: number): void {
    const start = total === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    const end   = Math.min(start + this.itemsPerPage - 1, total);
    const pages = Math.ceil(total / this.itemsPerPage);
    const setText = (id: string, v: string) => { const e = document.getElementById(id); if (e) e.textContent = v; };
    setText('res-start',      String(start));
    setText('res-end',        String(end));
    setText('res-total',      String(total));
    setText('res-page-label', total > 0 ? `Page ${this.currentPage} of ${pages}` : '');
    const prevBtn = document.getElementById('res-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('res-next-btn') as HTMLButtonElement | null;
    if (prevBtn) prevBtn.disabled = this.currentPage <= 1;
    if (nextBtn) nextBtn.disabled = this.currentPage >= pages;
  }

  private bindEvents(): void {
    document.getElementById('res-chart-from')?.addEventListener('change', (e) => {
      this.chartFromMonth = (e.target as HTMLInputElement).value;
      this.loadChartData(this.selectedListingId || undefined);
    });
    document.getElementById('res-chart-to')?.addEventListener('change', (e) => {
      this.chartToMonth = (e.target as HTMLInputElement).value;
      this.loadChartData(this.selectedListingId || undefined);
    });

    document.getElementById('res-start-date')?.addEventListener('change',    () => this.loadData());
    document.getElementById('res-end-date')?.addEventListener('change',      () => this.loadData());
    document.getElementById('res-status-filter')?.addEventListener('change', () => this.loadData());

    // Client-side search + channel filter over the already-loaded rows.
    const searchEl = document.getElementById('res-table-search') as HTMLInputElement | null;
    searchEl?.addEventListener('input', () => {
      this.tableSearch = searchEl.value;
      this.currentPage = 1;
      this.renderTable();
      const next = document.getElementById('res-table-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });
    document.getElementById('res-channel-filter')?.addEventListener('change', (e) => {
      this.tableChannel = (e.target as HTMLSelectElement).value;
      this.currentPage = 1;
      this.renderTable();
    });

    document.getElementById('res-property-filter')?.addEventListener('change', (e) => {
      this.selectedListingId = (e.target as HTMLSelectElement).value;
      this.loadChartData(this.selectedListingId || undefined);
    });


    document.getElementById('res-prev-btn')?.addEventListener('click', () => {
      if (this.currentPage > 1) { this.currentPage--; this.renderTable(); }
    });
    document.getElementById('res-next-btn')?.addEventListener('click', () => {
      const pages = Math.ceil(this.filteredReservations().length / this.itemsPerPage);
      if (this.currentPage < pages) { this.currentPage++; this.renderTable(); }
    });
  }

  private openDrawer(res: any): void {
    const overlay = document.getElementById('res-drawer-overlay');
    const drawer  = document.getElementById('res-drawer');
    const content = document.getElementById('res-drawer-content');
    if (!overlay || !drawer || !content) return;

    const close = () => { overlay.classList.remove('open'); drawer.classList.remove('open'); };
    overlay.onclick = close;

    const nights = res.check_in && res.check_out
      ? Math.round((new Date(res.check_out).getTime() - new Date(res.check_in).getTime()) / 86400000)
      : '—';
    const property = res.listings?.name || res.listing_name || '—';
    const channel  = res.channel_name || res.source || '—';
    const totalRaw = res.total_price ?? res.total_amount ?? res.total;
    const total    = (totalRaw != null && Number.isFinite(Number(totalRaw))) ? Number(totalRaw) : null;
    const fmt$ = (v: any) => v != null && v !== '' ? Formatters.formatCurrency(Number(v)) : '—';
    const row = (label: string, val: string) => `
      <div style="display:flex; justify-content:space-between; gap:12px; padding:8px 0; border-bottom:1px solid var(--border-soft); font-size:13px;">
        <span style="color:var(--text-muted);">${label}</span>
        <span style="font-weight:500; color:var(--text); text-align:right; min-width:0;">${val}</span>
      </div>`;

    content.innerHTML = `
      <div class="pf-drawer-header">
        <div style="flex:1; min-width:0;">
          <div class="pf-drawer-title">${Formatters.escapeHtml(res.guest_name || 'Guest')}</div>
          <div style="margin-top:6px; display:flex; gap:8px; flex-wrap:wrap;">
            <span class="pill ${this.statusPillClass(res.status)}">${res.status || 'confirmed'}</span>
            <span style="font-size:12px; color:var(--text-muted);">${channel}</span>
            ${res.confirmation_code ? `<span style="font-size:12px; color:var(--accent); font-weight:600;">#${Formatters.escapeHtml(res.confirmation_code)}</span>` : ''}
          </div>
        </div>
        <button id="res-close-btn" class="pf-close-btn">✕</button>
      </div>

      <div class="pf-drawer-body">
        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Stay Details</div>
          ${row('Property', Formatters.escapeHtml(property))}
          ${row('Check In', Formatters.formatDate(res.check_in))}
          ${row('Check Out', Formatters.formatDate(res.check_out))}
          ${row('Nights', String(nights))}
          ${res.adults != null ? row('Guests', `${res.adults} adults${res.children ? ', ' + res.children + ' children' : ''}`) : ''}
        </div>

        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Revenue Breakdown</div>
          ${row('Total Payout', fmt$(total))}
          ${res.base_amount != null ? row('Base Amount', fmt$(res.base_amount)) : ''}
          ${res.cleaning_fee != null ? row('Cleaning Fee', fmt$(res.cleaning_fee)) : ''}
          ${res.tax_amount != null ? row('Taxes', fmt$(res.tax_amount)) : ''}
          ${res.host_payout != null ? row('Host Payout', `<strong>${fmt$(res.host_payout)}</strong>`) : ''}
        </div>

        ${res.guest_email || res.guest_phone ? `
        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Guest Contact</div>
          ${res.guest_email ? row('Email', Formatters.escapeHtml(res.guest_email)) : ''}
          ${res.guest_phone ? row('Phone', Formatters.escapeHtml(res.guest_phone)) : ''}
        </div>` : ''}

        ${res.guest_note || res.notes ? `
        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Guest Note</div>
          <div style="font-size:13px; color:var(--text-muted); line-height:1.6; padding:8px 0;">${Formatters.escapeHtml(res.guest_note || res.notes || '')}</div>
        </div>` : ''}
      </div>
    `;

    overlay.classList.add('open');
    drawer.classList.add('open');
    content.querySelector('#res-close-btn')?.addEventListener('click', close);
  }
}
